<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';

// A comparação por template e por segmento: qual mensagem e qual público
// responderam melhor, no mesmo recorte. Uma dimensão por vez, e a aba pede de
// novo quando ela muda.
const props = defineProps({
  // A resposta de GET campanhas/comparar; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

// template | segment
const by = defineModel('by', { type: String, default: 'template' });

const { t } = useI18n();
const { number, percent } = useAnalyticsFormat();

const DIMENSIONS = [
  { key: 'template', label: 'CAMPANHAS.COMPARE.TEMPLATE' },
  { key: 'segment', label: 'CAMPANHAS.COMPARE.SEGMENT' },
];

const COLUMNS = [
  { key: 'campaigns', label: 'CAMPANHAS.COMPARE.CAMPAIGNS' },
  { key: 'sent', label: 'CAMPANHAS.TABLE.SENT' },
  { key: 'delivered', label: 'CAMPANHAS.TABLE.DELIVERED' },
  { key: 'read', label: 'CAMPANHAS.TABLE.READ' },
  { key: 'replied', label: 'CAMPANHAS.TABLE.REPLIED' },
  { key: 'reply_rate', label: 'CAMPANHAS.TABLE.REPLY_RATE', percent: true },
  { key: 'ai_attended', label: 'CAMPANHAS.COMPARE.AI_ATTENDED' },
  { key: 'failed', label: 'CAMPANHAS.TABLE.FAILED' },
  { key: 'opted_out', label: 'CAMPANHAS.TABLE.OPTED_OUT' },
];

// A resposta de outra dimensão (a de antes, enquanto a nova não chega) não é
// mostrada com o rótulo da nova.
const rows = computed(() =>
  props.report?.by === by.value ? props.report.rows || [] : []
);
const pending = computed(() => props.report && props.report.by !== by.value);

const noValue = computed(() =>
  by.value === 'segment'
    ? t('CAMPANHAS.COMPARE.NO_SEGMENT')
    : t('CAMPANHAS.COMPARE.NO_TEMPLATE')
);

const cell = (row, column) => {
  const value = row[column.key];
  if (typeof value !== 'number') return t('ANALYTICS_REPORTS.NO_DATA');
  return column.percent ? percent(value) : number(value);
};
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanhas-comparar"
    :title="t('CAMPANHAS.COMPARE.TITLE')"
  >
    <template #aside>
      <div
        role="group"
        :aria-label="t('CAMPANHAS.COMPARE.BY')"
        class="flex flex-wrap p-0.5 max-w-full rounded-lg border border-n-weak bg-n-alpha-1"
      >
        <button
          v-for="dimension in DIMENSIONS"
          :key="dimension.key"
          type="button"
          :data-test-id="`campanhas-comparar-${dimension.key}`"
          :aria-pressed="by === dimension.key"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
          :class="
            by === dimension.key
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="by = dimension.key"
        >
          {{ t(dimension.label) }}
        </button>
      </div>
    </template>

    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <p
      v-else-if="report && !pending && !rows.length"
      class="mb-0 text-sm text-n-slate-11"
    >
      {{ t('CAMPANHAS.COMPARE.EMPTY') }}
    </p>
    <!-- Dez colunas: a tabela rola sozinha, e não a página. -->
    <div v-else-if="rows.length" class="overflow-x-auto max-w-full">
      <table class="w-full text-sm min-w-[52rem]">
        <thead>
          <tr class="text-xs text-n-slate-11">
            <th class="py-2 font-medium pe-3 text-start">
              {{ t(DIMENSIONS.find(item => item.key === by).label) }}
            </th>
            <th
              v-for="column in COLUMNS"
              :key="column.key"
              class="px-2 py-2 font-medium text-end"
            >
              {{ t(column.label) }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="row in rows"
            :key="row.value ?? ''"
            :data-test-id="`campanhas-comparar-linha-${row.value ?? ''}`"
          >
            <td
              class="py-2 pe-3 truncate max-w-[16rem]"
              :class="row.value ? 'text-n-slate-12' : 'text-n-slate-11'"
            >
              {{ row.value || noValue }}
            </td>
            <td
              v-for="column in COLUMNS"
              :key="column.key"
              class="px-2 py-2 whitespace-nowrap text-end tabular-nums text-n-slate-12"
            >
              {{ cell(row, column) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </AnalyticsPanel>
</template>
